import { Button } from '@shared/ui/button';

/**
 * The recovery UI shown when a route or the whole app crashes.
 *
 * Uses the app's shadcn tokens (`--background`/`--foreground`/`--muted-*`)
 * rather than hard-coded colours, so it inherits each tenant's theme instead
 * of looking like a browser error page inside a branded PWA -- the same
 * reasoning that replaced `window.alert()` with sonner in Section 02.
 *
 * The error message itself is deliberately **not** rendered. It is a stack
 * trace or a minified React error to a reader, and this product's users are
 * magazine subscribers, not developers. It goes to the console (and topic
 * 13's reporting, once that exists) via ErrorBoundary's componentDidCatch.
 */
export function ErrorFallback({ reset, scope = 'page' }: ErrorFallbackProps) {
	return (
		<div
			role="alert"
			className="flex min-h-[50vh] w-full flex-col items-center justify-center gap-4 bg-background px-6 py-10 text-center text-foreground"
		>
			<div className="space-y-2">
				<h2 className="text-lg font-semibold">
					{scope === 'app'
						? 'Something went wrong'
						: "This page didn't load"}
				</h2>
				<p className="max-w-sm text-sm text-muted-foreground">
					{scope === 'app'
						? 'The app ran into an unexpected problem. Reloading usually fixes it.'
						: 'Something went wrong displaying this content. You can try again, or head back and pick something else.'}
				</p>
			</div>
			<div className="flex flex-wrap items-center justify-center gap-2">
				<Button onClick={reset}>Try again</Button>
				{scope === 'app' ? (
					<Button
						variant="outline"
						onClick={() => {
							window.location.reload();
						}}
					>
						Reload
					</Button>
				) : null}
			</div>
		</div>
	);
}

export interface ErrorFallbackProps {
	/**
	 * Clears the boundary and re-renders the subtree. For a route this is the
	 * router's own `reset`; for the app root it's the boundary's.
	 */
	reset: () => void;
	/**
	 * `'app'` is the last line of defence and offers a hard reload; `'page'`
	 * sits inside a still-working shell, so retrying in place is the sensible
	 * first move and the chrome around it stays usable.
	 */
	scope?: 'app' | 'page';
}
